import moment from 'moment-timezone';
import { forEachSeries } from 'p-iteration';

import ISAvailability from '../../Model/ISAvailability';
import {
  CAL_DOT_COM_TIME_ZONE,
  SLOT_DURATION_MINUTES,
  BOOKING_START_TIME_HOUR,
  BOOKING_START_TIME_MINUTE,
  BOOKING_END_TIME_HOUR,
  BOOKING_END_TIME_MINUTE,
} from './constants';

/**
 * Generate time slots for a specific date with availability data
 * Creates time slots based on configured slot duration from start time to end time for the given date
 * and availability data from the database
 * @param {moment} startOfTheDay - The start date&time to generate time slots for
 * @param {array} allAvailability - The availability data from the database for the given date
 * @returns {Array} Array of time slot objects with start and end times and availability data
 */
const createTimeSlotsForDate = async (startOfTheDay, allAvailability) => {
  const slotStartDateTime = moment(startOfTheDay).tz(CAL_DOT_COM_TIME_ZONE)
    .hour(BOOKING_START_TIME_HOUR)
    .minute(BOOKING_START_TIME_MINUTE)
    .second(0)
    .millisecond(0);

  const slotEndDateTime = moment(startOfTheDay).tz(CAL_DOT_COM_TIME_ZONE)
    .hour(BOOKING_END_TIME_HOUR)
    .minute(BOOKING_END_TIME_MINUTE)
    .second(0)
    .millisecond(0);

  const timeSlots = [];
  const currentDateStartTime = slotStartDateTime.clone();

  while (currentDateStartTime.isBefore(slotEndDateTime)) {
    const slotEndTime = currentDateStartTime.clone().add(SLOT_DURATION_MINUTES, 'minutes');

    if (slotEndTime.isAfter(slotEndDateTime)) {
      break;
    }
    const currentAvailabilityForSlot = allAvailability.find(
      (availability) => moment(availability.time.toDate()).isSame(currentDateStartTime),
    );

    timeSlots.push({
      start: currentDateStartTime.clone(),
      end: slotEndTime.clone(),
      availability: currentAvailabilityForSlot?.availability || 0,
    });
    currentDateStartTime.add(SLOT_DURATION_MINUTES, 'minutes');
  }
  return timeSlots;
};

/**
 * Generate sample availability events data for the calendar view date range
 * Creates time slots based on configured slot duration from start time to end time for each day
 * @param {Object} dateRange - Visible date range details on the calendar
 * @returns {Array} Array of availability events
 */
const generateAvailabilityEvents = async (dateRange) => {
  const { start, end } = dateRange;
  const startOfTheDay = moment(start).tz(CAL_DOT_COM_TIME_ZONE).startOf('day');
  const endOfTheDay = moment(end).tz(CAL_DOT_COM_TIME_ZONE).endOf('day');

  const availability = [];
  // Create array of days
  const days = Array.from({
    length: moment(endOfTheDay).diff(startOfTheDay, 'days') + 1, // Add 1 to include the end date
  }, (_, i) => moment(startOfTheDay).add(i, 'days'));

  const availabilityForDateRange = await ISAvailability.getAvailabilityForADateRange(
    startOfTheDay.toDate(), endOfTheDay.toDate(),
  );
  const allAvailabilityOfDB = [];
  availabilityForDateRange.docs.forEach((doc) => {
    allAvailabilityOfDB.push(...doc.data.availability);
  });

  await forEachSeries(days, async (date) => {
    const startDateTimeOfTheDay = moment(date).startOf('day');

    const timeSlots = await createTimeSlotsForDate(startDateTimeOfTheDay, allAvailabilityOfDB);

    // Create an availability event for each time slot
    timeSlots.forEach((slot, index) => {
      availability.push({
        id: `availability-${date.format('YYYY-MM-DD')}-${index}`,
        title: `Availability: ${slot.availability}`,
        start: moment(slot.start).format(),
        end: moment(slot.end).format(),
        availability: slot.availability,
        allDay: false,
      });
    });
  });
  return availability;
};

/**
 * Updates IS availability by calling the backend action 'calDotCom-changingISAvailability'
 * with the provided availability payload.
 *
 * @param {(action: string, body: any) => Promise<Response>} remote - Function to perform the server request.
 * @param {Array} availabilityData - Availability payload to persist.
 * @returns {Promise<{ success: boolean, err?: string }>} Normalized operation result.
 */
const changeISAvailability = async (remote, availabilityData) => {
  const response = await remote('calDotCom-changingISAvailability', availabilityData);
  if (!response.ok) {
    const { error } = await response.json();
    return { success: false, err: error };
  }
  return { success: true };
};

export {
  generateAvailabilityEvents,
  changeISAvailability,
};
